// src/pages/Clients.jsx
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Clients.css";
import {
  listClients,
  createClient,
  updateClient,
  deleteClient,
  mergeClients,
  getClientCount,
  getClientById,
} from "../api/clients";
import {
  listPortalRegistrations,
  approvePortalRegistration,
  rejectPortalRegistration,
} from "../api/clientPortalAdmin";
import { getCurrentSalonId, getCurrentSalonName } from "../utils/currentSalon";
import { digitsOnly, formatNANP } from "../utils/phone";
import { formatClientLabel } from "../utils/clientLabel";
import { countSearchLetters } from "../utils/normalizeSearch";
import ClientSearchAutocomplete from "../components/ClientSearchAutocomplete";
import NewClientModal from "../components/clients/NewClientModal";
import ClientImportWizard from "../components/clients/ClientImportWizard";
import MergeClientsModal from "../components/clients/MergeClientsModal";
import SBINModal from "../components/modals/SBINModal";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ModalCloseX from "../components/common/ModalCloseX";
import useMyPermissions from "../hooks/useMyPermissions";

const CLIENT_MANAGE_FALLBACK_ROLES = [
  "admin",
  "host",
  "owner",
  "manager",
  "reception",
  "receptionist",
  "independent_professional",
];
const CLIENT_CONTACT_FALLBACK_ROLES = CLIENT_MANAGE_FALLBACK_ROLES;
const PORTAL_ADMIN_ROLES = [
  "admin",
  "host",
  "owner",
  "manager",
  "independent_professional",
  "reception",
  "receptionist",
];
const HOUSE_ACCOUNT_MESSAGE =
  "House Account is a system client. No modifications are allowed.";
const isHouseAccountName = (name) =>
  (name || "").trim().toLowerCase() === "house account";
const NOTES_SCOPE_OPTIONS = [
  { value: "both", label: "Both" },
  { value: "profile", label: "Profile Notes only" },
  { value: "tab", label: "Notes tab only" },
];

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "What this page does",
    description: "Manage portal requests, search clients, and maintain records from one page.",
    anchorId: "mc-overview",
    bullets: [
      "Approve or reject online client registrations.",
      "Search clients by name, email, phone, or notes.",
      "Open a client to edit profile details.",
    ],
    tips: [
      "Use notes filters only when needed to keep searches fast.",
      "Merge duplicates before deleting records.",
    ],
  },
  {
    id: "portal",
    title: "Client portal requests",
    description: "Review new online signups before clients can book online.",
    anchorId: "mc-portal",
    bullets: [
      "Open a row to review registration details.",
      "Approve as a new client or merge into an existing client.",
      "Reject requests with an optional reason.",
    ],
    tips: ["Review pending requests daily to avoid booking delays."],
  },
  {
    id: "search",
    title: "Search and filters",
    description: "Find clients using basic search or advanced notes filters.",
    anchorId: "mc-search",
    bullets: [
      "Use name, email, or phone in the main search.",
      "Use Notes contains for profile and notes tab text.",
      "Adjust Search scope and snippet display when reviewing notes matches.",
    ],
    tips: ["Use clear keywords like allergy or preference for notes searches."],
  },
  {
    id: "results",
    title: "Results and actions",
    description: "Open client rows and use actions to maintain clean records.",
    anchorId: "mc-results",
    bullets: [
      "Edit updates profile, contact, and preferences.",
      "Merge combines duplicate client records.",
      "Delete removes records when permitted.",
    ],
    tips: ["Confirm the correct record before merge or delete actions."],
  },
  {
    id: "imports",
    title: "Import tools",
    description: "Use import tools to add or update many clients at once.",
    anchorId: "mc-import",
    bullets: [
      "Import Clients opens the bulk import wizard.",
      "Import History shows previous client import jobs and outcomes.",
      "New Client is best for one-off manual entries.",
    ],
    tips: ["Run a small test import first when using a new file format."],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "No results found",
    body: "Check spelling, try phone digits, or clear filters and run search again.",
  },
  {
    title: "Notes search blocked",
    body: "Some roles cannot search notes. Use basic search or ask an admin.",
  },
  {
    title: "Cannot edit a client",
    body: "Your role may not allow edits, or the selected record is a system client.",
  },
];

const TOOLTIPS = {
  salon: "Clients and imports on this page are tied to this salon.",
  search: "Search by client name, email, or phone.",
  advanced_filters: "Add notes filters when basic search is not enough.",
  notes_contains: "Find text inside profile notes and notes tab entries.",
  search_scope: "Choose where the notes keyword is checked.",
  snippet: "Show a short matched note preview in the results table.",
  portal_requests: "Approve or reject online registration requests.",
  submitted: "Date and time the request was submitted.",
  actions: "Use Edit, Merge, and Delete to maintain client records.",
};

const escapeRegExp = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderHighlightedSnippet = (snippet, keyword) => {
  if (!snippet) return "-";
  const term = (keyword || "").trim();
  if (!term) return snippet;
  const escaped = escapeRegExp(term);
  if (!escaped) return snippet;
  const regex = new RegExp(`(${escaped})`, "ig");
  const parts = String(snippet).split(regex);
  const lowerTerm = term.toLowerCase();
  return parts.map((part, index) =>
    part.toLowerCase() === lowerTerm ? (
      <mark
        key={`${part}-${index}`}
        className="rounded bg-amber-100 px-1 text-slate-900"
      >
        {part}
      </mark>
    ) : (
      part
    )
  );
};

export default function Clients() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const modalText = "text-base md:text-lg";
  const labelText =
    "text-sm md:text-base font-semibold tracking-wide text-slate-500";
  const fieldClass =
    "h-11 md:h-12 px-4 text-base md:text-lg rounded-xl border border-slate-300 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200";
  const sectionCard =
    "rounded-2xl border border-slate-200 bg-white shadow-sm p-6";
  const btnSecondary =
    "h-11 md:h-12 px-6 rounded-full border border-slate-300 text-slate-700 text-base md:text-lg font-semibold hover:bg-slate-50";
  const btnPrimary =
    "h-11 md:h-12 px-6 rounded-full bg-slate-900 text-white text-base md:text-lg font-semibold hover:bg-slate-800";
  const storedToken = token;
  const role = String(
    user?.role ||
      (typeof window !== "undefined" ? window.localStorage.getItem("role") : "") ||
      ""
  ).toLowerCase();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const { permissions: effectivePermissions } = useMyPermissions({
    enabled: Boolean(storedToken && salonId),
  });
  const permissionsLoaded = effectivePermissions !== null;
  const canEditClients = permissionsLoaded
    ? Boolean(effectivePermissions?.["clients.manage"])
    : CLIENT_MANAGE_FALLBACK_ROLES.includes(role);
  const canViewClientContact = permissionsLoaded
    ? Boolean(
        effectivePermissions?.["clients.view_contact"] ||
          effectivePermissions?.["clients.manage"]
      )
    : CLIENT_CONTACT_FALLBACK_ROLES.includes(role);
  const canManagePortal = permissionsLoaded
    ? Boolean(effectivePermissions?.["client_portal.manage"])
    : PORTAL_ADMIN_ROLES.includes(role);
  const showPhoneColumn = canViewClientContact;
  const baseColumnCount =
    3 + (showPhoneColumn ? 1 : 0) + (canEditClients ? 1 : 0);

  const salonOptions = salonId
    ? [{ id: salonId, name: salonName || `Salon ${salonId}` }]
    : [];

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inlineMessage, setInlineMessage] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRequestRef = useRef(0);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [notesKeyword, setNotesKeyword] = useState("");
  const [notesScope, setNotesScope] = useState("both");
  const [includeSnippet, setIncludeSnippet] = useState(true);
  const [notesKeywordError, setNotesKeywordError] = useState("");

  const [portalRegistrations, setPortalRegistrations] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");
  const [portalModalOpen, setPortalModalOpen] = useState(false);
  const [selectedPortalRegistration, setSelectedPortalRegistration] =
    useState(null);
  const [portalDecisionSaving, setPortalDecisionSaving] = useState(false);
  const [portalDecisionError, setPortalDecisionError] = useState("");
  const [portalRejectReason, setPortalRejectReason] = useState("");
  const [portalSearchTerm, setPortalSearchTerm] = useState("");
  const [portalSearchResults, setPortalSearchResults] = useState([]);
  const [portalSearchOpen, setPortalSearchOpen] = useState(false);
  const [portalSearchLoading, setPortalSearchLoading] = useState(false);
  const portalSearchRequestRef = useRef(0);
  const [portalMergeTarget, setPortalMergeTarget] = useState(null);

  const [toastMessage, setToastMessage] = useState("");
  const toastTimerRef = useRef(null);
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const showToast = (message) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToastMessage(message);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage("");
    }, 3000);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedClient, setSelectedClient] = useState(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [mergeTargetClient, setMergeTargetClient] = useState(null);

  const permissionError =
    "You don’t have permission to modify clients. Please contact the salon owner.";

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const refreshClients = async (overrides = {}) => {
    if (!salonId) {
      setClients([]);
      return;
    }
    const trimmed = (overrides.searchTerm ?? searchTerm).trim();
    const trimmedNotes = (overrides.notesKeyword ?? notesKeyword).trim();
    const hasNotes = Boolean(trimmedNotes);
    if (hasNotes && trimmedNotes.length < 2) {
      setNotesKeywordError("Please enter at least 2 characters.");
      setClients([]);
      setHasSearched(false);
      return;
    }
    setNotesKeywordError("");
    if (!trimmed && !hasNotes) {
      setClients([]);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    setInlineMessage("");
    setError("");
    try {
      const params = {
        salon_id: salonId,
        require_query: true,
        exclude_house: true,
      };
      if (trimmed) {
        params.search = trimmed;
      }
      if (hasNotes) {
        params.notes_keyword = trimmedNotes;
        params.notes_scope = notesScope;
        params.include_note_snippet = Boolean(includeSnippet);
      }
      const data = await listClients(params);
      setClients(Array.isArray(data) ? data : []);
      setHasSearched(true);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail?.code === "NOTES_SEARCH_FORBIDDEN") {
        setError("You don't have permission to search client notes.");
      } else {
        setError("Unable to load clients.");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadClientCount = async () => {
    if (!salonId) {
      setTotalCount(null);
      return;
    }
    try {
      const data = await getClientCount({ salon_id: salonId });
      const total = Number(data?.total);
      setTotalCount(Number.isNaN(total) ? null : total);
    } catch (err) {
      setTotalCount(null);
    }
  };

  const handleSelectClient = (client) => {
    if (!client) return;
    const label = formatClientLabel(client, showPhoneColumn);
    setSearchTerm(label);
    setSearchOpen(false);
    setInlineMessage("");
    refreshClients({ searchTerm: label });
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (hasSearched) {
      setClients([]);
      setHasSearched(false);
    }
    setInlineMessage("");
    setError("");
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchResults([]);
    setClients([]);
    setHasSearched(false);
    setSearchOpen(false);
    setInlineMessage("");
    setError("");
    setNotesKeyword("");
    setNotesScope("both");
    setIncludeSnippet(true);
    setNotesKeywordError("");
  };

  const handleNotesKeywordChange = (value) => {
    const trimmedValue = String(value || "").trim();
    setNotesKeyword(value);
    if (trimmedValue && trimmedValue.length < 2) {
      setNotesKeywordError("Please enter at least 2 characters.");
    } else if (notesKeywordError) {
      setNotesKeywordError("");
    }
    setInlineMessage("");
    setError("");
  };

  const handleRunSearch = () => {
    refreshClients();
  };

  useEffect(() => {
    if (!storedToken) {
      navigate("/login?next=/clients", { replace: true });
      return;
    }
    if (!salonId) {
      setError("Select a salon to view clients.");
      const timeout = setTimeout(() => {
        navigate("/my-salons", { replace: true });
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [storedToken, salonId, navigate]);

  useEffect(() => {
    if (!salonId) return;
    const params = new URLSearchParams(location.search || "");
    const editParam = params.get("edit_client");
    if (!editParam) return;
    const clientId = Number(editParam);
    if (!Number.isFinite(clientId) || clientId <= 0) {
      setInlineMessage("Client not found.");
      navigate("/clients", { replace: true });
      return;
    }
    if (!canEditClients) {
      setInlineMessage(permissionError);
      navigate("/clients", { replace: true });
      return;
    }
    let isActive = true;
    (async () => {
      try {
        const client = await getClientById(clientId);
        if (!isActive) return;
        if (
          !client ||
          (client.salon_id && Number(client.salon_id) !== Number(salonId))
        ) {
          setInlineMessage("Client not found.");
          return;
        }
        setModalMode("edit");
        setSelectedClient(client);
        setIsModalOpen(true);
      } catch (err) {
        if (!isActive) return;
        if (err?.response?.status === 403) {
          setInlineMessage(permissionError);
        } else {
          setInlineMessage("Client not found.");
        }
      } finally {
        if (isActive) {
          navigate("/clients", { replace: true });
        }
      }
    })();
    return () => {
      isActive = false;
    };
  }, [location.search, salonId, canEditClients, permissionError, navigate]);

  useEffect(() => {
    if (salonId) {
      loadClientCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonId]);

  const loadPortalRegistrations = async () => {
    if (!salonId || !canManagePortal) return;
    setPortalLoading(true);
    setPortalError("");
    try {
      const data = await listPortalRegistrations(salonId, "pending");
      setPortalRegistrations(Array.isArray(data) ? data : []);
    } catch (err) {
      setPortalRegistrations([]);
      setPortalError("Unable to load portal registrations.");
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    if (!salonId || !canManagePortal) return;
    loadPortalRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonId, canManagePortal]);

  const trimmedSearch = searchTerm.trim();
  const searchDigits = digitsOnly(trimmedSearch);
  const searchHasLetters = countSearchLetters(trimmedSearch) > 0;
  const searchIsName = searchHasLetters && trimmedSearch.length >= 3;
  const searchIsPhone = !searchHasLetters && searchDigits.length >= 4;
  const canAutocomplete = searchIsName || searchIsPhone;
  const trimmedNotesKeyword = notesKeyword.trim();
  const notesKeywordTooShort =
    Boolean(trimmedNotesKeyword) && trimmedNotesKeyword.length < 2;
  const showMatchedNoteColumn =
    Boolean(trimmedNotesKeyword) && !notesKeywordTooShort && includeSnippet;
  const columnCount = baseColumnCount + (showMatchedNoteColumn ? 1 : 0);
  const emptyTableMessage = Boolean(trimmedNotesKeyword) && !notesKeywordTooShort
    ? "No clients found matching that note keyword."
    : "No clients found for this search.";

  const portalTrimmedSearch = portalSearchTerm.trim();
  const portalSearchDigits = digitsOnly(portalTrimmedSearch);
  const portalSearchHasLetters = countSearchLetters(portalTrimmedSearch) > 0;
  const portalSearchIsName =
    portalSearchHasLetters && portalTrimmedSearch.length >= 3;
  const portalSearchIsPhone =
    !portalSearchHasLetters && portalSearchDigits.length >= 4;
  const portalCanAutocomplete = portalSearchIsName || portalSearchIsPhone;

  useEffect(() => {
    if (!salonId || !canAutocomplete) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    const requestId = ++searchRequestRef.current;
    setSearchLoading(true);
    const timerId = setTimeout(async () => {
      try {
        const params = {
          salon_id: salonId,
          search: trimmedSearch,
          limit: 20,
          exclude_house: true,
          require_query: true,
        };
        const data = await listClients(params);
        if (searchRequestRef.current !== requestId) return;
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Client autocomplete failed", err);
        if (searchRequestRef.current === requestId) {
          setSearchResults([]);
        }
      } finally {
        if (searchRequestRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    }, 250);
    return () => clearTimeout(timerId);
  }, [salonId, canAutocomplete, trimmedSearch]);

  useEffect(() => {
    if (!salonId || !portalModalOpen || !portalCanAutocomplete) {
      setPortalSearchResults([]);
      setPortalSearchLoading(false);
      return;
    }
    const requestId = ++portalSearchRequestRef.current;
    setPortalSearchLoading(true);
    const timerId = setTimeout(async () => {
      try {
        const params = {
          salon_id: salonId,
          search: portalTrimmedSearch,
          limit: 20,
          exclude_house: true,
          require_query: true,
        };
        const data = await listClients(params);
        if (portalSearchRequestRef.current !== requestId) return;
        const filtered = Array.isArray(data)
          ? data.filter(
              (client) => !isHouseAccountName(client?.full_name || "")
            )
          : [];
        setPortalSearchResults(filtered);
      } catch (err) {
        if (portalSearchRequestRef.current === requestId) {
          setPortalSearchResults([]);
        }
      } finally {
        if (portalSearchRequestRef.current === requestId) {
          setPortalSearchLoading(false);
        }
      }
    }, 250);
    return () => clearTimeout(timerId);
  }, [salonId, portalModalOpen, portalCanAutocomplete, portalTrimmedSearch]);

  const openPortalModal = (registration) => {
    if (!registration) return;
    setSelectedPortalRegistration(registration);
    setPortalDecisionError("");
    setPortalRejectReason("");
    setPortalSearchTerm("");
    setPortalSearchResults([]);
    setPortalSearchOpen(false);
    setPortalMergeTarget(null);
    setPortalModalOpen(true);
  };

  const closePortalModal = () => {
    if (portalDecisionSaving) return;
    setPortalModalOpen(false);
    setSelectedPortalRegistration(null);
    setPortalDecisionError("");
    setPortalRejectReason("");
    setPortalSearchTerm("");
    setPortalSearchResults([]);
    setPortalSearchOpen(false);
    setPortalMergeTarget(null);
  };

  const handlePortalApproveCreate = async () => {
    if (!selectedPortalRegistration?.id) return;
    setPortalDecisionSaving(true);
    setPortalDecisionError("");
    try {
      await approvePortalRegistration(selectedPortalRegistration.id, {
        mode: "create_new",
      });
      setPortalRegistrations((prev) =>
        prev.filter((row) => row.id !== selectedPortalRegistration.id)
      );
      showToast("Registration approved as a new client.");
      closePortalModal();
    } catch (err) {
      setPortalDecisionError(
        err?.response?.data?.detail || "Unable to approve registration."
      );
    } finally {
      setPortalDecisionSaving(false);
    }
  };

  const handlePortalApproveMerge = async () => {
    if (!selectedPortalRegistration?.id || !portalMergeTarget?.id) return;
    setPortalDecisionSaving(true);
    setPortalDecisionError("");
    try {
      await approvePortalRegistration(selectedPortalRegistration.id, {
        mode: "merge_existing",
        target_client_id: portalMergeTarget.id,
      });
      setPortalRegistrations((prev) =>
        prev.filter((row) => row.id !== selectedPortalRegistration.id)
      );
      showToast("Registration merged and approved.");
      closePortalModal();
    } catch (err) {
      setPortalDecisionError(
        err?.response?.data?.detail || "Unable to merge registration."
      );
    } finally {
      setPortalDecisionSaving(false);
    }
  };

  const handlePortalReject = async () => {
    if (!selectedPortalRegistration?.id) return;
    setPortalDecisionSaving(true);
    setPortalDecisionError("");
    try {
      await rejectPortalRegistration(selectedPortalRegistration.id, {
        reason: portalRejectReason?.trim() || undefined,
      });
      setPortalRegistrations((prev) =>
        prev.filter((row) => row.id !== selectedPortalRegistration.id)
      );
      showToast("Registration rejected.");
      closePortalModal();
    } catch (err) {
      setPortalDecisionError(
        err?.response?.data?.detail || "Unable to reject registration."
      );
    } finally {
      setPortalDecisionSaving(false);
    }
  };

  const portalPendingCount = portalRegistrations.length;

  const handleOpenCreate = () => {
    setModalMode("create");
    setSelectedClient(null);
    setIsModalOpen(true);
  };

  const handleOpenView = (client) => {
    setModalMode("edit");
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleOpenMerge = (client) => {
    if (!canEditClients) {
      setInlineMessage(permissionError);
      return;
    }
    if (isHouseAccountName(client?.full_name)) {
      setInlineMessage(HOUSE_ACCOUNT_MESSAGE);
      return;
    }
    setMergeTargetClient(client);
    setIsMergeOpen(true);
  };

  const handleDelete = async (client) => {
    if (!canEditClients) {
      setInlineMessage(permissionError);
      return;
    }
    if (isHouseAccountName(client?.full_name)) {
      setInlineMessage(HOUSE_ACCOUNT_MESSAGE);
      return;
    }
    const confirmed = window.confirm(
      `Delete ${client.full_name || "this client"}? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await deleteClient(client.id);
      await refreshClients();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      if (detail) {
        setInlineMessage(detail);
      } else if (err?.response?.status === 403) {
        setInlineMessage(permissionError);
      } else {
        setInlineMessage("Unable to delete client. Please try again.");
      }
    }
  };

  const handleSave = async (payload) => {
    if (modalMode === "create") {
      return createClient(payload);
    }
    if (!selectedClient?.id) return;
    return updateClient(selectedClient.id, payload);
  };

  const handleModalSave = async (payload) => {
    try {
      await handleSave(payload);
      await refreshClients();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail) {
        throw new Error(detail);
      }
      if (err?.response?.status === 403) {
        throw new Error(permissionError);
      }
      throw err;
    }
  };

  const canEditSelectedClient =
    canEditClients &&
    (!selectedClient || !isHouseAccountName(selectedClient?.full_name));

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <>
      <div className="manageSectionShell min-h-[calc(100vh-64px)] bg-slate-100 overflow-y-auto">
        <div className="manageSectionWide manage-section-wide manage-section-compact box-border">
          <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden min-h-[calc(115vh-140px)] flex flex-col">
            <div className="flex items-center justify-between bg-rose-500 px-6 py-4 relative">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-white">
                  Manage Clients
                </h1>
              </div>
              <div
                className="absolute right-4 top-3 z-50"
                onClick={handleClose}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <ModalCloseX onClick={handleClose} />
              </div>
            </div>
            <div
              className={`px-6 py-6 space-y-6 ${modalText} overflow-visible flex-1`}
            >
              <div
                id="mc-overview"
                className={getSectionClassName("mc-overview", "")}
              >
                <ReportHelpCard
                  title="Help Guide: Manage Clients"
                  subtitle="Manage client profiles, portal requests, and imports from one page."
                  sections={HELP_SECTIONS}
                  troubleshooting={TROUBLESHOOTING_ITEMS}
                  onJumpToSection={jumpToSectionWithHighlight}
                />
              </div>
              <div
                id="mc-import"
                className={getSectionClassName(
                  "mc-import",
                  "flex flex-wrap items-start justify-between gap-3"
                )}
              >
                <div>
                </div>
                {canEditClients && salonId && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate("/manager/manage/client-imports")}
                      className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                    >
                      Import History
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsImportOpen(true)}
                      className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                    >
                      Import Clients
                    </button>
                    <button
                      type="button"
                      onClick={handleOpenCreate}
                      className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                    >
                      New Client
                    </button>
                  </div>
                )}
              </div>

              {canManagePortal && salonId && (
                <div
                  id="mc-portal"
                  className={getSectionClassName("mc-portal", sectionCard)}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                          Client Portal Requests
                        </h2>
                        <InfoTooltip
                          text={TOOLTIPS.portal_requests}
                          label="Client Portal Requests"
                        />
                      </div>
                      <p className="text-sm md:text-base text-slate-500">
                        New online registrations need approval before clients can book online.
                      </p>
                    </div>
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs md:text-sm font-semibold text-slate-600">
                      {portalPendingCount} pending
                    </span>
                  </div>
                  {portalError && (
                    <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                      {portalError}
                    </div>
                  )}
                  {portalLoading ? (
                    <div className="mt-4 text-slate-500">Loading requests...</div>
                  ) : (
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full min-w-[760px] table-fixed">
                        <thead>
                          <tr className="text-left">
                            <th className="px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                              Name
                            </th>
                            <th className="px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                              Email
                            </th>
                            <th className="px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                              Phone
                            </th>
                            <th className="px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                              Username
                            </th>
                            <th className="px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                              <span className="inline-flex items-center gap-2">
                                Submitted
                                <InfoTooltip text={TOOLTIPS.submitted} label="Submitted" />
                              </span>
                            </th>
                            <th className="px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="border-t border-slate-100">
                          {portalRegistrations.length === 0 && (
                            <tr>
                              <td
                                colSpan={6}
                                className="px-4 py-6 text-slate-500 text-center"
                              >
                                No pending registrations.
                              </td>
                            </tr>
                          )}
                          {portalRegistrations.map((registration) => (
                            <tr
                              key={registration.id}
                              className="border-b border-slate-100 cursor-pointer hover:bg-slate-50"
                              onClick={() => openPortalModal(registration)}
                            >
                              <td className="px-4 py-3 font-semibold text-slate-900 text-sm md:text-base">
                                {registration.full_name || "-"}
                              </td>
                              <td className="px-4 py-3 text-slate-700 text-sm md:text-base">
                                {registration.email || "-"}
                              </td>
                              <td className="px-4 py-3 text-slate-700 text-sm md:text-base">
                                {registration.phone
                                  ? formatNANP(registration.phone)
                                  : "-"}
                              </td>
                              <td className="px-4 py-3 text-slate-700 text-sm md:text-base">
                                {registration.requested_username || "-"}
                              </td>
                              <td className="px-4 py-3 text-slate-500 text-sm md:text-base">
                                {registration.created_at
                                  ? new Date(registration.created_at).toLocaleString()
                                  : "-"}
                              </td>
                              <td className="px-4 py-3 text-slate-600 text-xs md:text-sm font-semibold uppercase">
                                {registration.status || "pending"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                  {error}
                </div>
              )}
              {inlineMessage && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm md:text-base">
                  {inlineMessage}
                </div>
              )}
              {loading ? (
                <div className="text-slate-500">Loading...</div>
              ) : (
                <div
                  id="mc-search"
                  className={getSectionClassName("mc-search", sectionCard)}
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      Clients
                    </h2>
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs md:text-sm font-semibold text-slate-600">
                      {totalCount === null ? "—" : totalCount} total
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className={`${labelText} inline-flex items-center gap-2`}>
                        Salon
                        <InfoTooltip text={TOOLTIPS.salon} label="Salon" />
                      </label>
                      <div
                        className={`w-full ${fieldClass} bg-slate-50 text-slate-700 flex items-center`}
                      >
                        {salonName || "Unknown salon"}
                        {salonId ? ` (ID ${salonId})` : ""}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 lg:col-span-2">
                      <label className={`${labelText} inline-flex items-center gap-2`}>
                        Search (name, email, phone)
                        <InfoTooltip text={TOOLTIPS.search} label="Search" />
                      </label>
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-2 md:flex-row md:items-start">
                          <div className="w-full md:flex-1">
                            <ClientSearchAutocomplete
                              value={searchTerm}
                              onChange={handleSearchChange}
                              results={searchResults}
                              onSelect={handleSelectClient}
                              isOpen={searchOpen}
                              onOpenChange={setSearchOpen}
                              placeholder="Type 3+ letters or 4+ digits..."
                              showPhone={showPhoneColumn}
                              inputClassName={`w-full ${fieldClass} text-sm md:text-lg`}
                              loading={searchLoading}
                              wrapClassName="manage-clients-search"
                              dropdownClassName="manage-clients-search-dropdown"
                            />
                          </div>
                          <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
                            <button
                              type="button"
                              onClick={handleRunSearch}
                              className={`${btnPrimary} flex-1 md:flex-none`}
                              disabled={
                                (!searchTerm.trim() &&
                                  !notesKeyword.trim()) ||
                                notesKeywordTooShort
                              }
                            >
                              Search
                            </button>
                            <button
                              type="button"
                              onClick={handleClearSearch}
                              className={`${btnSecondary} flex-1 md:flex-none`}
                              disabled={!searchTerm && !hasSearched && !notesKeyword.trim()}
                            >
                              Clear
                            </button>
                            <button
                              type="button"
                              onClick={() => setAdvancedOpen((prev) => !prev)}
                              className="h-11 md:h-12 w-full md:w-auto px-5 rounded-full border border-slate-300 text-slate-700 text-base md:text-lg font-semibold hover:bg-slate-50"
                            >
                              {advancedOpen ? "Hide Filters" : "Advanced Filters"}
                            </button>
                            <InfoTooltip
                              text={TOOLTIPS.advanced_filters}
                              label="Advanced Filters"
                            />
                          </div>
                        </div>
                        {advancedOpen && (
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                              <div className="flex flex-col gap-1 lg:col-span-2">
                                <label className={`${labelText} inline-flex items-center gap-2`}>
                                  Notes contains
                                  <InfoTooltip
                                    text={TOOLTIPS.notes_contains}
                                    label="Notes contains"
                                  />
                                </label>
                                <input
                                  type="text"
                                  value={notesKeyword}
                                  onChange={(e) =>
                                    handleNotesKeywordChange(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleRunSearch();
                                    }
                                  }}
                                  placeholder="sensitive / allergy / no acrylic"
                                  className={`w-full ${fieldClass}`}
                                />
                                {notesKeywordError && (
                                  <div className="text-xs text-rose-600">
                                    {notesKeywordError}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-2">
                                <label className={`${labelText} inline-flex items-center gap-2`}>
                                  Search scope
                                  <InfoTooltip text={TOOLTIPS.search_scope} label="Search scope" />
                                </label>
                                <div className="flex flex-wrap gap-2">
                                  {NOTES_SCOPE_OPTIONS.map((option) => {
                                    const isActive = notesScope === option.value;
                                    return (
                                      <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setNotesScope(option.value)}
                                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                                          isActive
                                            ? "border-slate-900 bg-slate-900 text-white"
                                            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                                        }`}
                                      >
                                        {option.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border border-slate-300 text-rose-600 focus:ring-0"
                                  checked={includeSnippet}
                                  onChange={(e) =>
                                    setIncludeSnippet(e.target.checked)
                                  }
                                />
                                Show note snippet
                                <InfoTooltip text={TOOLTIPS.snippet} label="Show note snippet" />
                              </label>
                              <span className="text-xs text-slate-500">
                                Searches profile notes and Notes tab entries.
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    id="mc-results"
                    className={getSectionClassName("mc-results", "mt-6")}
                  >
                    {!hasSearched ? (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center">
                        <div className="text-lg font-semibold text-slate-900">
                          Search clients
                        </div>
                        <div className="mt-2 text-sm text-slate-600">
                          Enter a name, phone, or email to find a client.
                        </div>
                      </div>
                    ) : (
                      <div className="clients-table-wrap">
                        <table className="clients-table hidden md:table">
                        <thead>
                          <tr className="text-left">
                            <th className="clients-col-name px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                              Name
                            </th>
                            <th className="clients-col-email px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                              Email
                            </th>
                            {showPhoneColumn && (
                              <th className="clients-col-phone px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                                Phone
                              </th>
                            )}
                            {showMatchedNoteColumn && (
                              <th className="clients-col-note px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                                Matched Note
                              </th>
                            )}
                            <th className="clients-col-created px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase">
                              Created
                            </th>
                            {canEditClients && (
                              <th className="clients-actions-header px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase text-right">
                                <span className="inline-flex items-center justify-end gap-2 w-full">
                                  Actions
                                  <InfoTooltip text={TOOLTIPS.actions} label="Actions" />
                                </span>
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="border-t border-slate-100">
                          {clients.length === 0 && (
                            <tr>
                              <td
                                colSpan={columnCount}
                                className="px-4 py-6 text-slate-500 text-center"
                              >
                                {emptyTableMessage}
                              </td>
                            </tr>
                          )}
                          {clients.map((client) => {
                            const isHouseAccount = isHouseAccountName(
                              client?.full_name
                            );
                            return (
                              <tr
                                key={client.id}
                                className="border-b border-slate-100 cursor-pointer"
                                onClick={() => handleOpenView(client)}
                              >
                                <td className="clients-col-name px-4 py-3 font-semibold text-slate-900 text-sm md:text-base">
                                  <span
                                    className="block truncate"
                                    title={client.full_name}
                                  >
                                    {client.full_name || "-"}
                                  </span>
                                </td>
                                <td className="clients-col-email px-4 py-3 text-slate-700 text-sm md:text-base">
                                  {client.email || "-"}
                                </td>
                                {showPhoneColumn && (
                                  <td className="clients-col-phone px-4 py-3 text-slate-700 text-sm md:text-base">
                                    {client.phone ? formatNANP(client.phone) : "-"}
                                  </td>
                                )}
                                {showMatchedNoteColumn && (
                                  <td className="clients-col-note px-4 py-3 text-slate-700 text-sm md:text-base">
                                    <div className="whitespace-pre-wrap">
                                      {renderHighlightedSnippet(
                                        client.matched_note_snippet,
                                        trimmedNotesKeyword
                                      )}
                                    </div>
                                  </td>
                                )}
                                <td className="clients-col-created px-4 py-3 text-slate-500 text-sm md:text-base">
                                  {client.created_at
                                    ? new Date(client.created_at).toLocaleString()
                                    : "-"}
                                </td>
                                {canEditClients && (
                                  <td
                                    className="clients-actions-cell px-4 py-3"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {isHouseAccount ? (
                                      <span className="inline-flex items-center rounded-full px-3 py-1 text-xs md:text-sm font-semibold bg-slate-200 text-slate-700">
                                        System
                                      </span>
                                    ) : (
                                      <div className="clients-actions-inline">
                                        <button
                                          type="button"
                                          onClick={() => handleOpenView(client)}
                                          className="h-9 px-4 rounded-full border border-slate-300 text-slate-700 text-sm md:text-base font-semibold hover:border-slate-400 hover:bg-slate-50"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleOpenMerge(client)}
                                          className="h-9 px-4 rounded-full border border-slate-300 text-slate-700 text-sm md:text-base font-semibold hover:border-slate-400 hover:bg-slate-50"
                                        >
                                          Merge
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDelete(client)}
                                          className="h-9 px-4 rounded-full text-rose-600 text-sm md:text-base font-semibold hover:bg-rose-50"
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                        </table>
                        <div className="space-y-3 md:hidden">
                          {clients.length === 0 && (
                            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center text-slate-500">
                              {emptyTableMessage}
                            </div>
                          )}
                          {clients.map((client) => {
                            const isHouseAccount = isHouseAccountName(
                              client?.full_name
                            );
                            return (
                              <div
                                key={client.id}
                                className="rounded-2xl border border-slate-200 bg-white px-4 py-4"
                              >
                                <button
                                  type="button"
                                  className="w-full text-left"
                                  onClick={() => handleOpenView(client)}
                                >
                                  <div className="font-semibold text-slate-900 break-words">
                                    {client.full_name || "-"}
                                  </div>
                                  <div className="mt-2 space-y-1 text-sm text-slate-600">
                                    <div className="break-words">{client.email || "-"}</div>
                                    {showPhoneColumn && (
                                      <div>{client.phone ? formatNANP(client.phone) : "-"}</div>
                                    )}
                                    <div>
                                      {client.created_at
                                        ? new Date(client.created_at).toLocaleString()
                                        : "-"}
                                    </div>
                                    {showMatchedNoteColumn && (
                                      <div className="whitespace-pre-wrap break-words text-slate-700">
                                        {renderHighlightedSnippet(
                                          client.matched_note_snippet,
                                          trimmedNotesKeyword
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </button>
                                <div
                                  className="mt-4"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {isHouseAccount ? (
                                    <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-slate-200 text-slate-700">
                                      System
                                    </span>
                                  ) : (
                                    <div className="flex flex-wrap items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleOpenView(client)}
                                        className="h-9 px-4 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:border-slate-400 hover:bg-slate-50"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleOpenMerge(client)}
                                        className="h-9 px-4 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:border-slate-400 hover:bg-slate-50"
                                      >
                                        Merge
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDelete(client)}
                                        className="h-9 px-2 rounded-full text-rose-600 text-sm font-semibold hover:bg-rose-50"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <NewClientModal
        isOpen={isModalOpen}
        mode={modalMode}
        initialClient={selectedClient}
        salons={salonOptions}
        canEdit={canEditSelectedClient}
        preferredSalonId={salonId}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedClient(null);
        }}
        onSave={handleModalSave}
      />

      <MergeClientsModal
        isOpen={isMergeOpen}
        onClose={() => {
          setIsMergeOpen(false);
          setMergeTargetClient(null);
        }}
        clients={clients}
        targetClient={mergeTargetClient}
        mergeClients={mergeClients}
        onMerged={async () => {
          await refreshClients();
          setInlineMessage("Clients merged successfully.");
        }}
      />

      <SBINModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        title="Import Clients"
        size="xxl"
      >
        <ClientImportWizard
          onComplete={() => {
            refreshClients();
          }}
          onClose={() => setIsImportOpen(false)}
          initialSalonId={salonId}
        />
      </SBINModal>

      <SBINModal
        isOpen={portalModalOpen}
        onClose={closePortalModal}
        title="Client Portal Request"
        size="xl"
      >
        {selectedPortalRegistration && (
          <div className="space-y-6">
            {portalDecisionError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {portalDecisionError}
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-slate-900">
                    Registration Details
                  </h3>
                  <p className="text-sm text-slate-500">
                    Review the submitted info before approving.
                  </p>
                </div>
                <div className="space-y-3 text-sm md:text-base">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-400">
                      Name
                    </div>
                    <div className="text-slate-900">
                      {selectedPortalRegistration.full_name || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-400">
                      Email
                    </div>
                    <div className="text-slate-700">
                      {selectedPortalRegistration.email || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-400">
                      Phone
                    </div>
                    <div className="text-slate-700">
                      {selectedPortalRegistration.phone
                        ? formatNANP(selectedPortalRegistration.phone)
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-400">
                      Username
                    </div>
                    <div className="text-slate-700">
                      {selectedPortalRegistration.requested_username || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-400">
                      Submitted
                    </div>
                    <div className="text-slate-700">
                      {selectedPortalRegistration.created_at
                        ? new Date(
                            selectedPortalRegistration.created_at
                          ).toLocaleString()
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-400">
                      Status
                    </div>
                    <div className="text-slate-700 uppercase text-xs font-semibold">
                      {selectedPortalRegistration.status || "pending"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-6">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-slate-900">
                    Approve as New Client
                  </h3>
                  <p className="text-sm text-slate-500">
                    Create a new client record with this information.
                  </p>
                  <button
                    type="button"
                    onClick={handlePortalApproveCreate}
                    disabled={portalDecisionSaving}
                    className="mt-3 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                  >
                    {portalDecisionSaving ? "Saving..." : "Approve + Create"}
                  </button>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <h3 className="text-base md:text-lg font-semibold text-slate-900">
                    Merge into Existing Client
                  </h3>
                  <p className="text-sm text-slate-500">
                    Search for a client and merge the registration details.
                  </p>
                  <div className="mt-3 space-y-3">
                    <ClientSearchAutocomplete
                      value={portalSearchTerm}
                      onChange={setPortalSearchTerm}
                      results={portalSearchResults}
                      onSelect={(client) => {
                        setPortalMergeTarget(client || null);
                        setPortalSearchOpen(false);
                        if (client) {
                          setPortalSearchTerm(formatClientLabel(client, true));
                        }
                      }}
                      isOpen={portalSearchOpen}
                      onOpenChange={setPortalSearchOpen}
                      placeholder="Search clients..."
                      showPhone
                      inputClassName="w-full h-10 px-3 rounded-xl border border-slate-300 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                      loading={portalSearchLoading}
                    />
                    {portalMergeTarget && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-sm font-semibold text-slate-900">
                          {portalMergeTarget.full_name || "Selected client"}
                        </div>
                        <div className="text-xs text-slate-500">
                          {portalMergeTarget.email || "-"}{" "}
                          -{" "}
                          {portalMergeTarget.phone
                            ? formatNANP(portalMergeTarget.phone)
                            : "No phone"}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setPortalMergeTarget(null);
                            setPortalSearchTerm("");
                          }}
                          className="mt-2 text-xs font-semibold text-slate-600 hover:text-slate-800"
                        >
                          Clear selection
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handlePortalApproveMerge}
                      disabled={portalDecisionSaving || !portalMergeTarget}
                      className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      {portalDecisionSaving ? "Saving..." : "Merge + Approve"}
                    </button>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <h3 className="text-base md:text-lg font-semibold text-slate-900">
                    Reject Registration
                  </h3>
                  <p className="text-sm text-slate-500">
                    Add an optional reason for the rejection.
                  </p>
                  <textarea
                    rows={3}
                    value={portalRejectReason}
                    onChange={(e) => setPortalRejectReason(e.target.value)}
                    placeholder="Optional reason..."
                    className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                  <button
                    type="button"
                    onClick={handlePortalReject}
                    disabled={portalDecisionSaving}
                    className="mt-3 rounded-full text-rose-600 px-4 py-2 text-sm font-semibold hover:bg-rose-50 disabled:opacity-60"
                  >
                    {portalDecisionSaving ? "Saving..." : "Reject"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </SBINModal>

      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 shadow-lg">
          {toastMessage}
        </div>
      )}
    </>
  );
}
